import React from 'react';
import {
  Box, Heading, HStack, Icon, Pressable, Text, VStack,
} from '@gluestack-ui/themed';
import { ChevronDown, ChevronUp } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { BulletList } from '@/common/components/BulletList';
import { useTenantSettingsStore } from '@/common/context/TenantSettingsStore';

export type OralExamFactsSummaryProps = {
  resolvedQuestionCount: number | null | undefined;
  resolvedSecondsPerQuestion: number | null | undefined;
  poolSize: number;
  /**
   * ``full`` — every briefing paragraph (lecturer / module preview).
   * ``compact`` — the "About this exam" section: chips + the first paragraph;
   * the rest folds behind "How the exam works" (student task preview).
   */
  variant?: 'full' | 'compact';
  testID?: string;
};

function formatMinutesFromSeconds(seconds: number): number {
  return Math.round((seconds / 60) * 10) / 10;
}

function OralExamFactsDetails({
  askedParagraph,
  testID,
}: {
  askedParagraph: string;
  testID: string;
}) {
  const { t } = useTranslation();

  return (
    <VStack space="sm">
      <Text size="sm" color="$textBody" testID={`${testID}-asked`}>
        {askedParagraph}
      </Text>
      <Text size="sm" color="$textBody" testID={`${testID}-placeholder-note`}>
        {t('common.oralExamFacts.placeholderNote')}
      </Text>
      <Text size="sm" color="$textBody">{t('common.oralExamFacts.para2')}</Text>
      <Text size="sm" color="$textBody">{t('common.oralExamFacts.conduct')}</Text>
      <Text size="sm" color="$textBody">{t('common.oralExamFacts.para3')}</Text>
    </VStack>
  );
}

const ATTEMPT_COUNTS_KEYS = ['started', 'notInLobbyReady', 'dropped'] as const;
const ATTEMPT_NOT_COUNTS_KEYS = ['cancelEarly', 'ourSide', 'practice'] as const;

function AttemptRuleList({ heading, items }: { heading: string; items: string[] }) {
  return (
    <VStack space="xs">
      <Text size="sm" fontFamily="$heading" color="$textHeading">{heading}</Text>
      <BulletList items={items} size="sm" />
    </VStack>
  );
}

function OralExamAttemptRules({ testID }: { testID: string }) {
  const { t } = useTranslation();
  const base = 'common.oralExamFacts.attemptRules';

  return (
    <VStack space="md" testID={testID}>
      <AttemptRuleList
        heading={t(`${base}.countsHeading`)}
        items={ATTEMPT_COUNTS_KEYS.map((key) => t(`${base}.counts.${key}`))}
      />
      <AttemptRuleList
        heading={t(`${base}.notCountsHeading`)}
        items={ATTEMPT_NOT_COUNTS_KEYS.map((key) => t(`${base}.notCounts.${key}`))}
      />
      <Text size="sm" color="$textSubtle">{t(`${base}.written`)}</Text>
    </VStack>
  );
}

function DisclosureRow({
  isOpen, label, onToggle, testID, children,
}: {
  isOpen: boolean;
  label: string;
  onToggle: () => void;
  testID: string;
  children: React.ReactNode;
}) {
  return (
    <VStack borderTopWidth={1} borderTopColor="$borderLight">
      <Pressable
        onPress={onToggle}
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        py="$2"
        accessibilityRole="button"
        accessibilityState={{ expanded: isOpen }}
        testID={testID}
      >
        <Text size="sm">{label}</Text>
        <Icon as={isOpen ? ChevronUp : ChevronDown} size="xs" color="$textSubtle" />
      </Pressable>
      {isOpen ? <Box pb="$3">{children}</Box> : null}
    </VStack>
  );
}

/**
 * OralExamFactsSummary
 * --------------------
 * System-authored oral-exam briefing (not the lecturer's ``task.description``).
 *
 * The briefing is two blocks split at the "you will be asked" sentence:
 * the first paragraph (what the exam session is + ID photo / recording) is always
 * visible; the asked-questions paragraph and the rest fold in the compact
 * student preview. Copy is 1:1 / 1:N agnostic — questions are asked, not
 * "an examiner will ask". Lecturer additional comments stay a separate field.
 */
export function OralExamFactsSummary({
  resolvedQuestionCount,
  resolvedSecondsPerQuestion,
  poolSize,
  variant = 'full',
  testID = 'oral-exam-facts-summary',
}: OralExamFactsSummaryProps) {
  const { t } = useTranslation();
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [attemptRulesOpen, setAttemptRulesOpen] = React.useState(false);
  const examAttemptsPerModule = useTenantSettingsStore(
    (state) => state.settings?.processSettings?.examAttemptsPerModule,
  );
  const showAttemptRules = variant === 'compact' && typeof examAttemptsPerModule === 'number';

  if (!resolvedQuestionCount || !resolvedSecondsPerQuestion) {
    return null;
  }

  const totalDurationMinutes = formatMinutesFromSeconds(
    resolvedQuestionCount * resolvedSecondsPerQuestion,
  );

  const perQuestionTime = resolvedSecondsPerQuestion < 60
    ? t('common.oralExamFacts.upToSecondsPerQuestion', { count: resolvedSecondsPerQuestion })
    : t('common.oralExamFacts.upToMinutesPerQuestion', {
      count: formatMinutesFromSeconds(resolvedSecondsPerQuestion),
    });

  const para1Key = poolSize > resolvedQuestionCount
    ? 'common.oralExamFacts.para1WithSubset'
    : 'common.oralExamFacts.para1';

  // First paragraph until the inserted break before "you will be asked".
  const leadParagraph = [
    t('common.oralExamFacts.intro'),
    t('common.oralExamFacts.processLead'),
  ].join(' ');

  const askedParagraph = t(para1Key, {
    count: resolvedQuestionCount,
    perQuestionTime,
    totalMinutes: totalDurationMinutes,
  });

  const perQuestionChip = resolvedSecondsPerQuestion < 60
    ? t('common.oralExamFacts.chipSecondsEach', { count: resolvedSecondsPerQuestion })
    : t('common.oralExamFacts.chipMinutesEach', {
      count: formatMinutesFromSeconds(resolvedSecondsPerQuestion),
    });

  const chips = [
    t('common.oralExamFacts.chipQuestions', { count: resolvedQuestionCount }),
    perQuestionChip,
    t('common.oralExamFacts.chipClosedBook'),
    t('common.oralExamFacts.chipRecorded'),
  ];

  const lead = (
    <Text size="sm" color="$textBody" testID={`${testID}-lead`}>
      {leadParagraph}
    </Text>
  );

  if (variant === 'compact') {
    return (
      <VStack space="sm" testID={testID}>
        <Heading size="sm">{t('common.oralExamFacts.title')}</Heading>
        <HStack flexWrap="wrap" space="xs" testID={`${testID}-chips`}>
          {chips.map((chip) => (
            <Box
              key={chip}
              bg="$bgSubtle"
              px="$2"
              py="$1"
              borderRadius="$full"
              mb="$1"
            >
              <Text size="2xs" fontWeight="medium" color="$textBody">{chip}</Text>
            </Box>
          ))}
        </HStack>
        {lead}
        <VStack borderBottomWidth={1} borderBottomColor="$borderLight">
          <DisclosureRow
            isOpen={detailsOpen}
            label={t('common.oralExamFacts.showDetails')}
            onToggle={() => setDetailsOpen((open) => !open)}
            testID={`${testID}-show-details`}
          >
            <OralExamFactsDetails askedParagraph={askedParagraph} testID={testID} />
          </DisclosureRow>
          {showAttemptRules ? (
            <DisclosureRow
              isOpen={attemptRulesOpen}
              label={t('common.oralExamFacts.attemptRules.show')}
              onToggle={() => setAttemptRulesOpen((open) => !open)}
              testID={`${testID}-show-attempt-rules`}
            >
              <OralExamAttemptRules testID={`${testID}-attempt-rules`} />
            </DisclosureRow>
          ) : null}
        </VStack>
      </VStack>
    );
  }

  return (
    <VStack space="sm" testID={testID}>
      {lead}
      <OralExamFactsDetails askedParagraph={askedParagraph} testID={testID} />
    </VStack>
  );
}
